import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Timer, ArrowLeft, Trophy, RotateCcw } from "lucide-react";

const ArithmeticSprintGame = () => {
    const navigate = useNavigate();
    const [gameState, setGameState] = useState('menu'); // menu, playing, gameover
    const [score, setScore] = useState(0);
    const [timeLeft, setTimeLeft] = useState(60);
    const [currentProblem, setCurrentProblem] = useState({ q: "", a: 0 });
    const [userAnswer, setUserAnswer] = useState("");
    const [feedback, setFeedback] = useState(""); // "correct", "wrong", or ""
    const inputRef = useRef(null);

    // Timer effect
    useEffect(() => {
        let timer;
        if (gameState === 'playing' && timeLeft > 0) {
            timer = setInterval(() => {
                setTimeLeft((prev) => prev - 1);
            }, 1000);
        } else if (timeLeft === 0 && gameState === 'playing') {
            endGame();
        }
        return () => clearInterval(timer);
    }, [gameState, timeLeft]);

    // Focus input when playing
    useEffect(() => {
        if (gameState === 'playing' && inputRef.current) {
            inputRef.current.focus();
        }
    }, [gameState]);

    const startGame = () => {
        setScore(0);
        setTimeLeft(60);
        setGameState('playing');
        generateProblem();
        setUserAnswer("");
        setFeedback("");
    };

    const endGame = () => {
        setGameState('gameover');
    };

    const generateProblem = () => {
        // Difficulty can scale with score?
        // Let's stick to rapid fire mental math:
        // + - * / (division results in integer)

        const ops = ['+', '-', '*'];
        // Add division only if easy to generate clean numbers
        if (Math.random() > 0.7) ops.push('/');

        const op = ops[Math.floor(Math.random() * ops.length)];
        let a, b, q, ans;

        switch (op) {
            case '+':
                a = Math.floor(Math.random() * 50) + 1;
                b = Math.floor(Math.random() * 50) + 1;
                ans = a + b;
                break;
            case '-':
                a = Math.floor(Math.random() * 50) + 10;
                b = Math.floor(Math.random() * (a - 1)) + 1; // Ensure positive result
                ans = a - b;
                break;
            case '*':
                a = Math.floor(Math.random() * 12) + 2;
                b = Math.floor(Math.random() * 9) + 2;
                ans = a * b;
                break;
            case '/':
                b = Math.floor(Math.random() * 9) + 2; // divisor 2-10
                ans = Math.floor(Math.random() * 12) + 1; // answer 1-12
                a = b * ans;
                break;
            default:
                break;
        }

        q = `${a} ${op === '*' ? '×' : op === '/' ? '÷' : op} ${b}`;
        setCurrentProblem({ q, a: ans });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const num = parseInt(userAnswer);
        if (isNaN(num)) return;

        if (num === currentProblem.a) {
            // Correct
            setScore(prev => prev + 10);
            setFeedback("correct");
            setTimeout(() => setFeedback(""), 200);
            generateProblem();
            setUserAnswer("");
        } else {
            // Wrong
            setScore(prev => Math.max(0, prev - 5)); // Penalty?
            setFeedback("wrong");
            setUserAnswer("");
            // Shake effect handled by CSS potentially, or just visual red flash
            setTimeout(() => setFeedback(""), 200);
        }
    };

    return (
        <div className="min-h-screen bg-indigo-50 flex flex-col items-center justify-center p-4">
             {/* Back Button */}
             <div className="absolute top-4 left-4">
                 <button
                    onClick={() => navigate("/games")}
                    className="flex items-center text-indigo-600 hover:text-indigo-800 font-bold transition-colors"
                 >
                     <ArrowLeft className="mr-2" /> Back to Games
                 </button>
             </div>

            {gameState === 'menu' && (
                <div className="bg-white p-8 rounded-2xl shadow-xl text-center max-w-md w-full animate-fade-in">
                    <Trophy className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
                    <h1 className="text-4xl font-black text-gray-800 mb-2">Arithmetic Sprint</h1>
                    <p className="text-gray-600 mb-8 text-lg">
                        Solve as many problems as you can in 60 seconds!
                    </p>
                    <button
                        onClick={startGame}
                        className="w-full py-4 bg-indigo-600 text-white rounded-xl font-bold text-xl hover:bg-indigo-700 hover:scale-105 transition-all shadow-lg flex items-center justify-center"
                    >
                        <Play className="mr-2" /> Start Sprint
                    </button>
                </div>
            )}

            {gameState === 'playing' && (
                <div className="w-full max-w-lg">
                    {/* Header: Timer and Score */}
                    <div className="flex justify-between items-center mb-8 px-4">
                        <div className="flex items-center space-x-2">
                             <div className={`
                                flex items-center justify-center w-16 h-16 rounded-full border-4 font-bold text-2xl
                                ${timeLeft <= 10 ? 'border-red-500 text-red-600 animate-pulse' : 'border-indigo-500 text-indigo-700'}
                                bg-white shadow-md
                             `}>
                                 {timeLeft}
                             </div>
                             <span className="text-sm font-semibold text-gray-400 uppercase">Sec</span>
                        </div>
                        
                        <div className="text-right">
                            <span className="block text-sm font-semibold text-gray-400 uppercase">Score</span>
                            <span className="text-4xl font-black text-indigo-900">{score}</span>
                        </div>
                    </div>

                    {/* Question Card */}
                    <div className="bg-white rounded-3xl shadow-2xl p-8 mb-6 relative overflow-hidden">
                        {feedback === 'correct' && (
                            <div className="absolute inset-0 bg-green-100 opacity-50 z-0"></div>
                        )}
                        {feedback === 'wrong' && (
                            <div className="absolute inset-0 bg-red-100 opacity-50 z-0"></div>
                        )}

                        <div className="relative z-10 text-center">
                            <h2 className="text-6xl font-bold text-gray-800 mb-8 font-mono">
                                {currentProblem.q}
                            </h2>

                            <form onSubmit={handleSubmit}>
                                <input
                                    ref={inputRef}
                                    type="number"
                                    value={userAnswer}
                                    onChange={(e) => setUserAnswer(e.target.value)}
                                    className={`
                                        w-full text-center text-5xl font-bold py-4 rounded-xl border-4 outline-none transition-colors
                                        ${feedback === 'wrong' ? 'border-red-400 bg-red-50 text-red-900' : 'border-indigo-200 focus:border-indigo-500 text-gray-800'}
                                    `}
                                    placeholder="?"
                                />
                            </form>
                            <p className="mt-4 text-gray-400 text-sm">Press Enter to submit</p>
                        </div>
                    </div>
                </div>
            )}

            {gameState === 'gameover' && (
                <div className="bg-white p-8 rounded-2xl shadow-xl text-center max-w-md w-full animate-fade-in">
                    <div className="mb-6">
                        <span className="text-6xl">🏁</span>
                    </div>
                    <h2 className="text-3xl font-bold text-gray-800 mb-2">Time's Up!</h2>
                    <p className="text-gray-500 mb-6">Great effort!</p>
                    
                    <div className="bg-indigo-50 rounded-xl p-6 mb-8">
                        <span className="block text-sm font-bold text-indigo-400 uppercase tracking-wider mb-1">Final Score</span>
                        <span className="text-5xl font-black text-indigo-900">{score}</span>
                    </div>

                    <div className="space-y-3">
                        <button
                            onClick={startGame}
                            className="w-full py-3 bg-indigo-600 text-white rounded-xl font-bold text-lg hover:bg-indigo-700 transition-colors shadow-md flex items-center justify-center"
                        >
                            <RotateCcw className="mr-2 w-5 h-5" /> Play Again
                        </button>
                        <button
                            onClick={() => navigate("/games")}
                            className="w-full py-3 bg-white border-2 border-gray-200 text-gray-600 rounded-xl font-bold text-lg hover:bg-gray-50 transition-colors"
                        >
                            Back to Games
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ArithmeticSprintGame;
